// OSINT-inntak + berikelse (oppskrift 1: ustrukturert data → entiteter/koordinater).
// En ekstern skraper (nyheter/sosiale medier) POSTer OsintItem-er; hubben kjører
// dem gjennom denne DETERMINISTISKE uttrekks-pipelinen (MGRS/lat-lon/nøkkelord).
// LLM-basert entitetsuttrekk kan erstatte extractEntities senere (samme signatur).
import { z } from 'zod';
// 'mgrs' er et dual-package: Node får CJS-bygget (navngitt import krasjer i ESM-
// loaderen → default = module.exports), mens Vite får ESM-bygget (navngitte
// eksporter, INGEN default). Namespace-import + fallback dekker begge trygt
// (Node-ESM, vitest og nettleser-bundel).
import * as mgrsNs from 'mgrs';
const mgrsToPoint = mgrsNs.toPoint
    ?? mgrsNs.default.toPoint;
export const OsintItemSchema = z.object({
    id: z.string().max(128),
    source: z.string().max(200),
    url: z.string().max(2048).optional(),
    text: z.string().max(20000),
    tsMs: z.number(),
});
export function parseOsintBatch(raw) {
    const arr = Array.isArray(raw) ? raw : raw?.items;
    if (!Array.isArray(arr))
        return [];
    const out = [];
    for (const item of arr.slice(0, 2000)) {
        const r = OsintItemSchema.safeParse(item);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
// Nøkkelord → grov militær kategori (utvidbar). Norsk + engelsk.
const KEYWORDS = {
    stridsvogn: 'kjøretøy', tank: 'kjøretøy', kjøretøy: 'kjøretøy', lastebil: 'kjøretøy', pansret: 'kjøretøy',
    drone: 'luft', uav: 'luft', helikopter: 'luft', jagerfly: 'luft', fly: 'luft',
    soldat: 'personell', tropp: 'personell', styrke: 'personell', infanteri: 'personell',
    fartøy: 'sjø', båt: 'sjø', ubåt: 'sjø', skip: 'sjø', korvett: 'sjø',
    skudd: 'ild', artilleri: 'ild', rakett: 'ild', missil: 'ild', eksplosjon: 'ild',
};
const MGRS_RE = /\b\d{1,2}[C-X][A-Z]{2}\d{2,10}\b/g;
const LATLON_RE = /(-?\d{1,2}\.\d{3,})[,\s]+(-?\d{1,3}\.\d{3,})/g;
/** Trekker ut koordinater (MGRS + lat,lon) og nøkkelord fra fri tekst. */
export function extractEntities(text) {
    const coords = [];
    const seen = new Set();
    const push = (lat, lon, from) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            return;
        if (Math.abs(lat) > 90 || Math.abs(lon) > 180)
            return;
        const key = `${lat.toFixed(5)},${lon.toFixed(5)}`;
        if (seen.has(key))
            return;
        seen.add(key);
        coords.push({ lat, lon, from });
    };
    for (const m of text.matchAll(MGRS_RE)) {
        try {
            const p = mgrsToPoint(m[0]);
            if (Array.isArray(p) && p.length === 2)
                push(p[1], p[0], 'mgrs');
        }
        catch { /* ikke gyldig MGRS */ }
    }
    for (const m of text.matchAll(LATLON_RE)) {
        push(parseFloat(m[1]), parseFloat(m[2]), 'latlon');
    }
    const lower = ` ${text.toLowerCase()} `;
    const keywords = new Set();
    for (const [word, cat] of Object.entries(KEYWORDS)) {
        if (new RegExp(`[^a-zæøå]${word}[^a-zæøå]`).test(lower))
            keywords.add(cat);
    }
    return { coords, keywords: [...keywords].sort() };
}
/** Kjente våpen-/plattform-betegnelser (leksikon, utvidbart). */
export const WEAPON_LEXICON = [
    'T-72', 'T-80', 'T-90', 'BMP-1', 'BMP-2', 'BMP-3', 'BTR', 'BTR-80', 'BTR-82',
    'BM-21', 'Grad', 'Iskander', 'Shahed', 'Shahed-136', 'Orlan', 'Orlan-10',
    'Lancet', 'S-300', 'S-400', 'Pantsir', 'Buk', 'Tornado', 'Msta', 'Giatsint',
    'MiG-29', 'MiG-31', 'Su-24', 'Su-25', 'Su-34', 'Su-35', 'Ka-52', 'Mi-8', 'Mi-24', 'Mi-28',
];
const DTG_RE = /\b\d{6}[A-Z]\s?[A-Z]{3}\d{2}\b/g; // 191430ZJUL26
const DATE_RE = /\b\d{1,2}\.\d{1,2}\.\d{2,4}\b/g; // 19.07.2026
const QTY_RE = /\b(\d{1,4})\s+(stridsvogn\w*|kjøretøy\w*|soldat\w*|dron\w*|fartøy\w*|helikopter\w*|rakett\w*|artilleri\w*)/gi;
const UNIT_RE = /\bHV-\d{1,2}(?:-\d{3})?\b|\b(?:Tropp|Lag|Kompani|Bataljon|Brigade|Område)\s+\d+\b/gi;
const PHONE_RE = /\b(?:\+47[ ]?)?(?:\d{2}[ ]?){3}\d{2}\b/g; // norsk 8-sifret
/**
 * Trekk ut strukturerte, typede entiteter fra fri tekst: koordinater, DTG/dato,
 * antall+type, enhetsbetegnelser, kjente våpenplattformer og telefonnr.
 * Dedupliseres per (type,value).
 */
export function extractNamedEntities(text) {
    const out = [];
    const seen = new Set();
    const add = (type, value, normalized) => {
        const key = `${type}:${(normalized ?? value).toLowerCase()}`;
        if (seen.has(key))
            return;
        seen.add(key);
        out.push({ type, value, ...(normalized ? { normalized } : {}) });
    };
    for (const c of extractEntities(text).coords)
        add('koordinat', `${c.lat.toFixed(5)},${c.lon.toFixed(5)}`, `${c.lat.toFixed(5)},${c.lon.toFixed(5)}`);
    for (const m of text.matchAll(DTG_RE))
        add('dtg', m[0]);
    for (const m of text.matchAll(DATE_RE))
        add('dtg', m[0]);
    for (const m of text.matchAll(QTY_RE))
        add('antall', m[0].trim());
    for (const m of text.matchAll(UNIT_RE))
        add('enhet', m[0], m[0].toUpperCase());
    for (const m of text.matchAll(PHONE_RE))
        add('telefon', m[0].trim());
    const hay = text.toLowerCase();
    for (const w of WEAPON_LEXICON)
        if (hay.includes(w.toLowerCase()))
            add('våpen', w);
    return out;
}
/** OSINT-punkt → lavtillits Track (vises som ukjent punkt på kartet). */
export function osintToTracks(item) {
    const { coords, keywords } = extractEntities(item.text);
    return coords.map((c, i) => ({
        id: `osint:${item.id}:${i}`,
        kind: 'point',
        affiliation: 'unknown',
        lat: c.lat,
        lon: c.lon,
        sidc: 'SUGP-----------',
        label: `OSINT: ${item.source}`,
        tsMs: item.tsMs,
        confidence: 0.2, // åpen kilde → lav tillit til systemet har verifisert
        trust: 0.2,
        source: `osint:${item.source}`,
        extra: {
            tekst: item.text.slice(0, 240),
            ...(keywords.length ? { kategorier: keywords.join(', ') } : {}),
            ...(item.url ? { url: item.url } : {}),
            koordinatkilde: c.from,
        },
    }));
}
//# sourceMappingURL=osint.js.map